import { searchTypeProcessing } from '@/api/product';
import Button from '@/components/Button';
import Input from '@/components/Form/FormInput';
import { useJump } from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import { colors } from '@/theme/zeelool.theme';
import { IS_DE_FR } from '@/utils/constants';
import { setSession } from '@/utils/tools';
import { InputAdornment } from '@mui/material';
import { SearchOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter, useSearchParams } from 'next/navigation';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchStore } from './store/search-store';

// 表单数据类型
interface SearchFormData {
  searchVal: string;
}

interface KeywordItem {
  is_attr?: string;
  is_product?: string;
  key: string;
  value?: string;
  label: string;
}

interface SearchInputRef {
  clickWord: (item: any, type: string, flag: number) => void;
}

const SearchInput = forwardRef<
  SearchInputRef,
  {
    onClose: () => void;
    isShowSearchBtn?: boolean;
    isWeb?: boolean;
    onFocus?: () => void;
    onInputChange?: (value: string) => void;
    onBlur?: () => void;
    isDarkMode?: boolean;
  }
>(
  (
    { onClose, isShowSearchBtn = true, isWeb = false, onFocus, onInputChange, onBlur, isDarkMode },
    ref,
  ) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const toJumpTarget = useJump();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const t = useTranslations('layout.header.search');
    const [matchKeywords, setMatchKeywords] = useState<KeywordItem[]>([]);
    const [showAutoSearch, setShowAutoSearch] = useState(false); // 是否展示自动搜索弹层
    const [defaultSearch, setDefaultSearch] = useState(''); // 轮询默认搜索词，作为输入框的 placeholder
    const [defaultSearchIdx, setDefaultSearchIdx] = useState(0); // 轮询下标
    const [isClickWords, setIsClickWords] = useState(0); // 是否为点击搜索词，用来埋点统计
    const [searchType, setSearchType] = useState(0); // 用于搜索结果页统计
    const [searchWordType, setSearchWordType] = useState(''); // 用于区分历史记录搜索还是热词搜索
    const { trackEvent } = useReporting();
    const [defaultSearchTimer, setDefaultSearchTimer] = useState<NodeJS.Timeout | null>(null);

    // 从store获取数据
    const searchData = useSearchStore((state) => state.searchData);
    const searchHistory = useSearchStore((state) => state.searchHistory);
    const setSearchHistory = useSearchStore((state) => state.setSearchHistory);
    const keywordsMaps = useSearchStore((state) => state.keywordsMaps);
    const hotProductList = useSearchStore((state) => state.hotProductList);

    // react-hook-form setup
    const methods = useForm<SearchFormData>({
      defaultValues: {
        searchVal: '',
      },
    });

    // 监听表单值变化
    const searchVal = methods.watch('searchVal');

    // 点击搜索词
    const clickWord = (keyword: any, type: string, flag: number) => {
      setSearchWordType(type);
      setIsClickWords(flag);
      if (keyword) {
        methods.setValue('searchVal', keyword);
      }
    };

    // 使用useImperativeHandle暴露方法给父组件
    useImperativeHandle(ref, () => ({
      clickWord,
    }));

    // 获取搜索类型
    // TODO: 这里的枚举是什么？？
    const getSearchType = () => {
      if (isClickWords) {
        if (searchWordType === 'history') {
          return 2;
        }
        if (searchWordType) {
          return 4;
        }
        return 1;
      } else {
        if (searchVal) {
          return 1;
        }
        if (defaultSearch) {
          return 3;
        }
        return 1;
      }
    };

    useEffect(() => {
      if (!searchVal.length) {
        setShowAutoSearch(false);
        setMatchKeywords([]);
        // 通知父组件输入值为空
        onInputChange?.('');
        return;
      }

      setSearchType(1);
      setSearchWordType('');

      // 通知父组件输入值变化
      onInputChange?.(searchVal);

      // 如果是点击搜索词，不展示自动搜索
      if (!isClickWords) {
        // 搜索框输入内容，展示自动搜索
        setShowAutoSearch(true);
      }

      const val = searchVal.toLowerCase();
      const matched: KeywordItem[] = [];

      // 输入时进行key值匹配,相同key值仅push一次
      keywordsMaps.forEach((item) => {
        if (item.key.indexOf(val) === 0 && !matched.filter((obj) => obj.key === item.key).length) {
          matched.push(item);
        }
      });

      // 排序
      matched.sort((a, b) => {
        if (a.key > b.key) return 1;
        if (a.key < b.key) return -1;
        return 0;
      });

      setMatchKeywords(matched);
    }, [searchVal, keywordsMaps, isClickWords]);

    const handleClose = () => {
      setShowAutoSearch(false);
      setIsClickWords(0);
      setSearchType(0);
      setSearchWordType('');
      methods.setValue('searchVal', ''); // 清空表单值
      onClose();
    };

    // 搜索跳转
    const goSearch = async () => {
      const searchRaw = searchVal || defaultSearch;
      if (!searchRaw.trim().length) {
        methods.setValue('searchVal', '');
        return;
      }

      // 去除首尾空格
      const search = searchRaw.trim();

      // 是否是点击搜索框中的搜索词直接搜索
      setSearchType(getSearchType());

      // 更新搜索历史
      setHistory(search);

      // 匹配热搜词，如果 jump_type === 4，直接跳转外链
      let matchedHotItem = null;
      if (hotProductList && hotProductList.length > 0) {
        matchedHotItem = hotProductList.find(
          (item: any) => item.keywords && item.keywords === search,
        );

        if (matchedHotItem?.jump && matchedHotItem.jump.jump_type === 4) {
          // 使用 toJumpTarget 方法跳转外链
          toJumpTarget({
            jump_type: matchedHotItem.jump.jump_type,
            Jump_target: matchedHotItem.jump.path,
          });
          handleClose();
          return;
        }
      }

      // 如果不能匹配热搜词，调用后端接口进行匹配
      if (!matchedHotItem) {
        try {
          const { data, code } = (await searchTypeProcessing({ search })) as {
            data: { jump_type?: number; path?: string };
            code: number;
          };
          if (code === 200 && data) {
            if (data.jump_type === 4 && data.path) {
              // 使用 toJumpTarget 方法跳转外链
              toJumpTarget({
                jump_type: data.jump_type,
                Jump_target: data.path,
              });
              handleClose();
              return;
            }
          }
        } catch {
          // 接口调用失败，继续执行原有的搜索逻辑
        }
      }

      // 检查是否与当前路由相同
      const currentKeywords = searchParams.get('keywords');
      const currentSearchType = searchParams.get('search_type');

      if (
        search === currentKeywords &&
        searchType &&
        currentSearchType &&
        +currentSearchType === searchType
      ) {
        handleClose();
        return;
      }

      methods.setValue('searchVal', '');

      const searchTypeMap: Record<string, string> = {
        '1': '普通搜索',
        '2': 'Recently Searched',
        '3': '普通搜索',
        '4': 'Popular Searches',
      };

      // 埋点统计
      trackEvent(
        {
          eventName: 'SearchBtn_Click',
          properties: {
            search_type: searchTypeMap[searchType.toString()] || '普通搜索',
            search_text: search,
          },
        },
        [ProviderType.SENSORS],
      );

      // 路由跳转
      const currentPath = window.location.pathname;
      const isGoodsDetail = currentPath.includes('/goods-detail/');
      const skuParam = isGoodsDetail ? `&sku=${currentPath.split('/').pop()}` : '';
      handleClose();
      if (searchType) {
        router.push(`/search-result?keywords=${search}&search_type=${searchType}${skuParam}`);
      } else {
        router.push(`/search-result?keywords=${search}${skuParam}`);
      }
    };

    // 设置默认搜索
    const updateDefaultSearch = useCallback((data: any, idx: number) => {
      if (defaultSearchTimer) {
        clearTimeout(defaultSearchTimer);
      }

      // 使用传入的data参数而不是依赖searchData状态
      const defaultKeywords = data?.defaultKeywords || [];
      if (defaultKeywords.length > 0) {
        const currentKeyword = defaultKeywords[idx]?.name || '';
        setDefaultSearch(currentKeyword);
      }

      const timer = setTimeout(() => {
        if (idx >= defaultKeywords.length - 1) {
          setDefaultSearchIdx(0);
          updateDefaultSearch(data, 0);
        } else {
          const nextIdx = idx + 1;
          setDefaultSearchIdx(nextIdx);
          updateDefaultSearch(data, nextIdx);
        }
      }, 3000);

      setDefaultSearchTimer(timer);
    }, []);

    // 处理特殊字符和空格
    const handleSpecialStr = (value: string) => {
      // 特殊字符转为1个空格
      // 多个空格处理为1个
      // 去除首尾空格
      // 某些特殊法语字符的兼容，需要汇总特殊字符（à â ç é è ê ë î ï ô œ ù û ü ÿ À Â Ç É È Ê Ë Î Ï Ô Œ Ù Û Ü Ÿ）
      const regx = IS_DE_FR ? /[^a-zA-Z\u00C0-\u00FF0-9-]/g : /[^a-zA-Z0-9-]/g;
      return value.replace(regx, ' ').replace(/\s+/g, ' ');
    };

    // 处理回车键搜索
    const handleKeyPress = (e: React.KeyboardEvent) => {
      // 当你按 Enter 键时，实际上发生了两件事
      // 你的 onKeyDown 处理器触发 goSearch() 函数
      // HTML <form> 元素的默认提交行为也被触发
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault(); // 阻止表单默认提交行为
        goSearch();
      }
    };

    // 监听searchData变化，启动默认搜索轮播
    useEffect(() => {
      if (searchData?.defaultKeywords?.length && defaultSearchIdx === 0) {
        updateDefaultSearch(searchData, 0);
      }
    }, [searchData, updateDefaultSearch]);

    // 初始化数据
    // useEffect(() => {
    //   // 聚焦搜索框
    //   setTimeout(() => {
    //     if (searchInputRef.current) {
    //       searchInputRef.current.focus();
    //     }
    //   }, 500);
    // }, []);

    // 自动搜索
    const autoSearch = (item: KeywordItem) => {
      methods.setValue('searchVal', '');
      setMatchKeywords([]);
      setHistory(item.label);
      handleClose();
      if (item.is_product && item.is_product === '1') {
        setSession('zl_goodsDetailEnterPageScene', '搜索浮层进入');
        router.push(`/goods-detail/${item.value}`);
      } else {
        const currentPath = window.location.pathname;
        const isGoodsDetail = currentPath.includes('/goods-detail/');
        const skuParam = isGoodsDetail ? `&sku=${currentPath.split('/').pop()}` : '';
        router.push(`/search-result?keywords=${item.label}${skuParam}`);
      }
    };

    // 设置搜索历史
    const setHistory = (search: string) => {
      let newSearchHistory = [...searchHistory];

      if (!newSearchHistory.find((item) => item === search)) {
        // 搜索历史中没有当前搜索项直接插入到数组第一项
        newSearchHistory.unshift(search);
        if (newSearchHistory.length > 5) {
          newSearchHistory = newSearchHistory.slice(0, 5);
        }
      } else {
        // 搜索历史中含有当前搜索项,找到索引删除，再插入到第一项
        const index = newSearchHistory.findIndex((item) => item === search);
        const item = newSearchHistory.splice(index, 1)[0];
        newSearchHistory.unshift(item);
      }

      setSearchHistory(newSearchHistory);
    };

    return (
      <>
        <div className="w-[68%] h-full lg:w-[100%]">
          {/* 使用form标签，防止浏览器自动填充(自动填充的情况： My Card 删除卡弹框，自动填充密码时，会把帐号自动填充到这里) */}
          <form autoComplete="off">
            <FormProvider {...methods}>
              <Input
                name="searchVal"
                formItemClassName="mb-0"
                control={methods.control}
                ref={searchInputRef}
                formatInput={handleSpecialStr}
                onKeyDown={handleKeyPress}
                onFocus={() => {
                  // 一旦聚焦，就认为是开始要输入了，不再是之前的click关键词的状态
                  setIsClickWords(0);
                  onFocus && onFocus();
                }}
                onBlur={onBlur}
                placeholder={defaultSearch || t('searchPlaceholder')}
                size="small"
                autoComplete="off"
                clearable
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        {isWeb ? (
                          <button
                            type="button"
                            aria-label={t('search')}
                            className={`p-0 m-0 border-0 bg-transparent inline-flex items-center justify-center cursor-pointer ${isDarkMode ? 'text-white' : 'text-[#282828]'}`}
                            onClick={() => {
                              goSearch();
                            }}
                          >
                            <SearchOutlined className="text-[21px]" aria-hidden />
                          </button>
                        ) : (
                          <SearchOutlined
                            className={`text-[21px] cursor-pointer ${isDarkMode ? 'text-white' : 'text-[#282828]'}`}
                            aria-hidden
                          />
                        )}
                      </InputAdornment>
                    ),
                    style: {
                      height: isWeb ? '36px' : '32px',
                      backgroundColor: isWeb && isDarkMode ? 'rgba(25,25,27,1)' : '#f5f6f7',
                      color: isWeb && isDarkMode ? 'rgba(255,255,255,0.8)' : '#282828',
                      borderRadius: '16px',
                    },
                  },
                  htmlInput: {
                    'aria-label': t('searchForProducts'),
                    maxLength: 30,
                    autoCorrect: 'off',
                    spellCheck: 'false',
                    name: 'search',
                    // 这是一个自定义属性，明确告诉浏览器这是搜索字段
                    'data-form-type': 'search',
                    // 这是一个特殊属性，专门用于告诉密码管理器（如LastPass）忽略这个字段
                    'data-lpignore': 'true',
                  },
                }}
                sx={
                  isWeb
                    ? {
                        '&': {
                          marginTop: 0,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor:
                            isWeb && isDarkMode
                              ? 'rgba(25,25,27,1) !important'
                              : '#f5f6f7 !important',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor:
                            isWeb && isDarkMode
                              ? 'rgba(25,25,27,1) !important'
                              : '#f5f6f7 !important',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor:
                            isWeb && isDarkMode
                              ? 'rgba(25,25,27,1) !important'
                              : '#f5f6f7 !important',
                        },
                        '& .MuiOutlinedInput-input': {
                          paddingLeft: '0px !important',
                          fontSize: '14px',
                        },
                      }
                    : {
                        '&': {
                          marginTop: 0,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primaryPurple,
                          borderRadius: '16px',
                        },
                        '& .MuiOutlinedInput-input': {
                          paddingLeft: '0px !important',
                          '&::placeholder': {
                            color: '#A9A9A9',
                          },
                        },
                      }
                }
              />
            </FormProvider>

            {/* 自动搜索建议 */}
            {showAutoSearch && matchKeywords.length > 0 && (
              <div className="absolute z-1 w-[80%] lg:w-[100%] max-h-37.5 bg-white overflow-y-auto shadow-md">
                {matchKeywords.slice(0, 5).map((item, index) => (
                  <div key={index} className="p-[8px] text-[14px]" onClick={() => autoSearch(item)}>
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>
        {isShowSearchBtn && (
          <div className="ml-[10px]">
            <Button
              variant="contained"
              color="primary"
              onClick={methods.handleSubmit(() => goSearch())}
              aria-label={t('searchForProducts')}
              sx={{
                height: '32px',
                fontSize: '13px',
              }}
            >
              {t('search')}
            </Button>
          </div>
        )}
      </>
    );
  },
);

export default SearchInput;
